// src/components/AiAssistant/HistoriqueConversations.tsx
//
// Modal d'historique des conversations IA.
// Permet d'ouvrir / renommer (inline) / archiver / supprimer une
// conversation, ainsi que de tout effacer (RGPD) ou créer une
// nouvelle conversation.
//
// Mai 2026.
import React, { useEffect, useState } from 'react';
import {
  X, Plus, Trash2, Archive, MessageSquare, Edit2, Check,
  RotateCcw, AlertTriangle, Loader2,
} from 'lucide-react';
import {
  aiAssistantService,
  AiConversation,
} from '../../services/aiAssistantService';
import toast from '../../utils/toast';

interface Props {
  open: boolean;
  onClose: () => void;
  currentConversationId: string | null;
  onOpenConversation: (id: string) => void;
  onNewConversation: () => void;
  onConversationDeleted: (id: string) => void;
}

// Helper : date relative simple ("il y a 2 min", "hier", "il y a 3 j")
function dateRelative(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMin / 60);
  const diffJ = Math.floor(diffH / 24);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffH < 24) return `il y a ${diffH} h`;
  if (diffJ === 1) return 'hier';
  if (diffJ < 7) return `il y a ${diffJ} j`;
  return d.toLocaleDateString('fr-FR');
}

const HistoriqueConversations: React.FC<Props> = ({
  open,
  onClose,
  currentConversationId,
  onOpenConversation,
  onNewConversation,
  onConversationDeleted,
}) => {
  const [conversations, setConversations] = useState<AiConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [showArchivees, setShowArchivees] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitre, setEditingTitre] = useState('');
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const data = await aiAssistantService.listConversations({
        archivees: showArchivees,
      });
      setConversations(Array.isArray(data) ? data : []);
    } catch (e: any) {
      toast.error("Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadConversations();
      setConfirmClearAll(false);
      setConfirmDeleteId(null);
      setEditingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showArchivees]);

  const handleStartEdit = (c: AiConversation) => {
    setEditingId(c.id);
    setEditingTitre(c.titre || '');
  };

  const handleSaveEdit = async (id: string) => {
    const newTitre = editingTitre.trim();
    if (!newTitre) {
      toast.error('Le titre ne peut pas être vide');
      return;
    }
    try {
      await aiAssistantService.updateConversation(id, { titre: newTitre });
      setConversations(prev =>
        prev.map(c => (c.id === id ? { ...c, titre: newTitre } : c)),
      );
      setEditingId(null);
      toast.success('Titre mis à jour');
    } catch {
      toast.error('Échec de la mise à jour');
    }
  };

  const handleArchive = async (c: AiConversation) => {
    try {
      await aiAssistantService.updateConversation(c.id, { archivee: !c.archivee });
      toast.success(c.archivee ? 'Conversation restaurée' : 'Conversation archivée');
      await loadConversations();
    } catch {
      toast.error("Échec de l'archivage");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await aiAssistantService.deleteConversation(id);
      setConversations(prev => prev.filter(c => c.id !== id));
      setConfirmDeleteId(null);
      onConversationDeleted(id);
      toast.success('Conversation supprimée');
    } catch {
      toast.error('Échec de la suppression');
    }
  };

  const handleClearAll = async () => {
    try {
      await aiAssistantService.clearAllConversations();
      setConversations([]);
      setConfirmClearAll(false);
      // Réinitialiser la conversation courante côté parent
      if (currentConversationId) onConversationDeleted(currentConversationId);
      toast.success('Toutes les conversations ont été supprimées');
    } catch {
      toast.error('Échec de la suppression globale');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 flex items-center justify-center text-white">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">Historique des conversations</h3>
              <p className="text-xs text-gray-500">
                {conversations.length} conversation{conversations.length > 1 ? 's' : ''}
                {showArchivees ? ' archivée' + (conversations.length > 1 ? 's' : '') : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-3 border-b flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onNewConversation(); onClose(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow hover:opacity-90 transition"
            >
              <Plus className="w-3.5 h-3.5" />
              Nouvelle conversation
            </button>
            <button
              onClick={() => setShowArchivees(v => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                showArchivees
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Archive className="w-3.5 h-3.5" />
              {showArchivees ? 'Archivées' : 'Actives'}
            </button>
          </div>
          <button
            onClick={() => setConfirmClearAll(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition"
            title="Tout supprimer (RGPD)"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Tout supprimer
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0 p-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Chargement...
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">
                {showArchivees
                  ? 'Aucune conversation archivée'
                  : 'Aucune conversation pour le moment'}
              </p>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {conversations.map((c) => {
                const isActive = c.id === currentConversationId;
                const isEditing = editingId === c.id;
                const isConfirmingDelete = confirmDeleteId === c.id;
                return (
                  <li
                    key={c.id}
                    className={`group rounded-xl border transition ${
                      isActive
                        ? 'border-indigo-300 bg-indigo-50/50'
                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 p-2.5">
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={editingTitre}
                              onChange={(e) => setEditingTitre(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveEdit(c.id);
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              autoFocus
                              className="flex-1 text-sm px-2 py-1 border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            <button
                              onClick={() => handleSaveEdit(c.id)}
                              className="p-1 rounded-md text-emerald-600 hover:bg-emerald-50"
                              title="Enregistrer"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
                              title="Annuler"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              onOpenConversation(c.id);
                              onClose();
                            }}
                            className="text-left w-full"
                          >
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {c.titre || 'Conversation sans titre'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {c.nbMessages} message{c.nbMessages > 1 ? 's' : ''}
                              {' · '}
                              {dateRelative(c.updatedAt)}
                              {c.contexteModule ? ` · ${c.contexteModule}` : ''}
                            </p>
                          </button>
                        )}
                      </div>

                      {/* Actions */}
                      {!isEditing && (
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                          <button
                            onClick={() => handleStartEdit(c)}
                            className="p-1.5 rounded-md text-gray-500 hover:bg-white hover:text-blue-600"
                            title="Renommer"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleArchive(c)}
                            className="p-1.5 rounded-md text-gray-500 hover:bg-white hover:text-amber-600"
                            title={c.archivee ? 'Restaurer' : 'Archiver'}
                          >
                            {c.archivee ? (
                              <RotateCcw className="w-3.5 h-3.5" />
                            ) : (
                              <Archive className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(c.id)}
                            className="p-1.5 rounded-md text-gray-500 hover:bg-white hover:text-red-600"
                            title="Supprimer"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Confirmation suppression individuelle */}
                    {isConfirmingDelete && (
                      <div className="px-3 pb-2.5 -mt-1">
                        <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 flex items-center justify-between gap-2">
                          <p className="text-xs text-red-700 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            Supprimer définitivement ?
                          </p>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-xs px-2 py-1 rounded-md text-gray-600 hover:bg-white"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => handleDelete(c.id)}
                              className="text-xs px-2 py-1 rounded-md bg-red-600 text-white hover:bg-red-700"
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Confirmation Tout supprimer */}
        {confirmClearAll && (
          <div className="px-5 py-3 border-t bg-red-50 flex items-center justify-between gap-2 flex-shrink-0">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800">
                Supprimer <b>toutes</b> vos conversations ? Cette action est définitive.
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={() => setConfirmClearAll(false)}
                className="text-xs px-3 py-1.5 rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleClearAll}
                className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Confirmer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoriqueConversations;
